import { Component, OnInit, Input, SimpleChanges } from '@angular/core';
interface BalanceSheetData {
   label: string;
   data: Record<number, number>;
   children?: BalanceSheetData[];
}

interface QuarterlyBalanceSheetData {
   label: string;
   data: Record<string, number>;
   children?: QuarterlyBalanceSheetData[];
   isExpanded?: boolean;
}
@Component({
    selector: 'app-f-cashflow-table',
    templateUrl: './f-cashflow-table.component.html',
    styleUrls: ['./f-cashflow-table.component.scss'],
    standalone: false
})
export class FCashflowTableComponent implements OnInit {
   @Input() _isAnnual: boolean = true;
   @Input() grossMarginAnnualArray: any[] = [];

   @Input() grossMarginQuarterlyArray: any[] = [];
   @Input() data: any[] = [];
   @Input() quarterlydata: any[] = [];
   processedQuarterlyData: any[] = [];
   filteredAnnualData: any[] = [];
   filteredQuarterlyData: any[] = [];

   noDataAnnual: boolean = false;
   noDataQuarterly: boolean = false;
   processedData: any[] = [];
   years: number[] = [];
   quarters: string[] = [];
   quarters2: string[] = [];

   expandedRows: Record<string, boolean> = {};
   expandedRows2: Record<string, boolean> = {};

   expandedItems: Set<string> = new Set();
   constructor() {}

   ngOnInit(): void {
    this.processAndSetData();
   }

   ngOnChanges(changes: SimpleChanges): void {
    if (changes['quarterlydata'] && changes['quarterlydata'].currentValue) {
      //  console.log('Quarterly data changed:', changes['quarterlydata'].currentValue);
       this.processAndSetData();
    }
 }

 private processAndSetData() {
  this.processedData = this.processDataWithHierarchy(this.data);
  this.processedQuarterlyData = this.processDataWithHierarchy(this.quarterlydata);
//   console.log('Processed Quarterly Data:', this.processedQuarterlyData);

  // Extract latest years from annual data
  this.years = this.extractLatestYears(this.data);

  // Extract latest 4 quarters from quarterly data
  this.quarters = this.extractLatestQuarters(this.quarterlydata);
  this.quarters2 = this.extractLatestQuarters(this.quarterlydata);

  this.noDataAnnual = !this.hasAnnualData(this.data);
  this.noDataQuarterly = !this.hasQuarterlyData(this.quarterlydata);
}

   private initializeExpansionState(items: QuarterlyBalanceSheetData[]) {
      if (!items?.length) return;

      items.forEach((item) => {
         if (item?.children?.length) {
            item.isExpanded = false;
            this.initializeExpansionState(item.children);
         }
      });
   }
   getFormattedValue = (data: { [key: string]: string } | undefined, quarter: string): string => {
      if (!data || !data[quarter]) return '-'; // Return dash if no data or missing quarter

      let value = parseFloat(data[quarter]); // Convert string to number

      if (isNaN(value)) return '-'; // Return dash if value is not a valid number

      // Multiply the number by 10,00,000
      value *= 1_000_000;

      const absValue = Math.abs(value); // Get absolute value
      const formattedValue = (val: number) => val.toFixed(2).replace(/\.?0+$/, ''); // Ensure up to 2 decimal places

      let result: string;

      // Handle billions
      if (absValue >= 1_000_000_000) {
         result = formattedValue(absValue / 1_000_000_000) + ' B';
      }
      // Handle millions
      else if (absValue >= 1_000_000) {
         result = formattedValue(absValue / 1_000_000) + ' M';
      }
      // Handle smaller values
      else {
         result = formattedValue(absValue); // Use absValue instead of value
      }

      // Show negative values in parentheses (without minus sign)
      return value < 0 ? `(${result})` : result;
   };





   formatNumber(value: string): string {
      if (!value) return '-';
      try {
         const number = parseFloat(value);
         return new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: 0,
         }).format(number);
      } catch {
         return '-';
      }
   }

   processDataWithHierarchy(data: BalanceSheetData[]): BalanceSheetData[] {
      const hierarchicalData: BalanceSheetData[] = [];
      const includedLabels = new Set<string>();

      // Helper function to find item by label
      const findItem = (label: string): BalanceSheetData | undefined => {
         const item = data.find((item) => item.label === label);
         if (item) {
            includedLabels.add(label);
         }
         return item;
      };

      // Helper function to create children array with type safety
      const createChildren = (
         items: (BalanceSheetData | undefined)[]
      ): BalanceSheetData[] =>
         items.filter((item): item is BalanceSheetData => item !== undefined);

      // Add netIncomeStartingLine first (if it exists)
      const netIncomeStartingLine = findItem('netIncomeStartingLine');
      if (netIncomeStartingLine) {
         hierarchicalData.push(netIncomeStartingLine);
      }

      // Process Net Operating Cash Flow section
      const netOperatingCashFlow = findItem('netOperatingCashFlow');
      if (netOperatingCashFlow) {
         const stockBasedCompensation = findItem('stockBasedCompensation');
         const depreciationAmortization = findItem('depreciationAmortization');
         const otherFundsNonCashItems = findItem('otherFundsNonCashItems');
         const changesinWorkingCapital = findItem('changesinWorkingCapital');

         netOperatingCashFlow.children = createChildren([
            stockBasedCompensation,
            depreciationAmortization,
            otherFundsNonCashItems,
            changesinWorkingCapital,
         ]);
         hierarchicalData.push(netOperatingCashFlow);
      }

      // Process Net Investing Cash Flow section
      const netInvestingCashFlow = findItem('netInvestingCashFlow');
      if (netInvestingCashFlow) {
         netInvestingCashFlow.children = createChildren([
            findItem('capex'),
            findItem('otherInvestingCashFlowItemsTotal'),
         ]);
         hierarchicalData.push(netInvestingCashFlow);
      }

      // Process Net Cash Financing Activities section
      const netCashFinancingActivities = findItem('netCashFinancingActivities');
      if (netCashFinancingActivities) {
         netCashFinancingActivities.children = createChildren([
            findItem('issuanceReductionDebtNet'),
            findItem('otherFundsFinancingItems'),
            findItem('Repurchases of common stock'),
            findItem('cashDividendsPaid'),
            findItem('cashTaxesPaid'),
         ]);
         hierarchicalData.push(netCashFinancingActivities);
      }

      // Find remaining items that were not included
      const remainingItems = data.filter(
         (item) => !includedLabels.has(item.label)
      );
      hierarchicalData.push(...remainingItems);

      return hierarchicalData;
   }

   processDataWithHierarchy2(
      data: QuarterlyBalanceSheetData[]
   ): QuarterlyBalanceSheetData[] {
      const hierarchicalData: QuarterlyBalanceSheetData[] = [];
      const includedLabels = new Set<string>();

      // Helper function to find item by label
      const findItem = (
         label: string
      ): QuarterlyBalanceSheetData | undefined => {
         const item = data.find((item) => item.label === label);
         if (item) includedLabels.add(label);
         return item;
      };

      // Helper function to create children array with type safety
      const createChildren = (
         items: (QuarterlyBalanceSheetData | undefined)[]
      ): QuarterlyBalanceSheetData[] =>
         items.filter(
            (item): item is QuarterlyBalanceSheetData => item !== undefined
         );

      // Process Net Income section
      const netIncome = findItem('Net income');
      if (netIncome) {
         const adjustmentsToReconcileNetIncome = findItem(
            'Adjustments to Reconcile Net Income (Loss) to Cash Provided by (Used in) Operating Activities'
         );
         if (adjustmentsToReconcileNetIncome) {
            adjustmentsToReconcileNetIncome.children = createChildren([
               findItem('Depreciation and amortization'),
               findItem('Share-Based Compensation Expense'),
               findItem('Other'),
            ]);
         }

         const changesInOperatingAssetsAndLiabilities = findItem(
            'Changes in Operating Assets & Liabilities'
         );
         if (changesInOperatingAssetsAndLiabilities) {
            changesInOperatingAssetsAndLiabilities.children = createChildren([
               findItem('Accounts receivable, net'),
               findItem('Vendor non-trade receivables'),
               findItem('Inventories'),
               findItem('Other current and non-current liabilities'),
               findItem('Cash generated by operating activities'),
            ]);
         }

         netIncome.children = createChildren([
            adjustmentsToReconcileNetIncome,
            changesInOperatingAssetsAndLiabilities,
         ]);
         hierarchicalData.push(netIncome);
      }

      // Process Investing Activities section
      const investingActivities = findItem('Investing Activities');
      if (investingActivities) {
         investingActivities.children = createChildren([
            findItem('Purchases of marketable securities'),
            findItem('Proceeds from maturities of marketable'),
            findItem('Proceeds from sales of marketable'),
            findItem(
               'Payments for acquisition of - Acquisition of businesses, net of cash acquired'
            ),
            findItem('Other'),
            findItem('Cash Generated by'),
         ]);
         hierarchicalData.push(investingActivities);
      }

      // Process Financial Activities section
      const financialActivities = findItem('Financial Activities');
      if (financialActivities) {
         financialActivities.children = createChildren([
            findItem(
               'Payments for taxes related to net share settlement of equity awards'
            ),
            findItem('Payments for dividends and dividend equivalents'),
            findItem('Repurchases of common stock'),
            findItem('Proceeds from issuance of term debts, net'),
            findItem('Proceeds from commercial paper, net'),
            findItem('Other'),
            findItem('Cash Generated by Investing Activities'),
         ]);
         hierarchicalData.push(financialActivities);
      }

      // Find remaining items that were not included
      const remainingItems = data.filter(
         (item) => !includedLabels.has(item.label)
      );

      // Append remaining items at the bottom
      hierarchicalData.push(...remainingItems);

      return hierarchicalData;
   }

   toggleRow(item: BalanceSheetData) {
      this.expandedRows[item.label] = !this.expandedRows[item.label];
   }
   toggleRow2(item: QuarterlyBalanceSheetData) {
      this.expandedRows2[item.label] = !this.expandedRows2[item.label];
   }

   private openRows: Set<string> = new Set();

   getYears(data: Record<number, number>): number[] {
      return Object.keys(data).map((year) => parseInt(year)); // Get years from the data object keys
   }

   private extractLatestYears(annualData: any[]): number[] {
      if (!annualData || annualData.length === 0) {
         return [];
      }

      // Collect all unique years from all data items
      const allYears = new Set<number>();
      annualData.forEach(item => {
         if (item.data) {
            Object.keys(item.data).forEach(year => {
               const yearNum = parseInt(year);
               if (!isNaN(yearNum)) {
                  allYears.add(yearNum);
               }
            });
         }
      });

      // Convert to array and sort years chronologically
      const sortedYears = Array.from(allYears).sort((a, b) => a - b);

      // Return the latest 5 years (last 5 from the sorted array)
      return sortedYears.slice(-5);
   }

   private extractLatestQuarters(quarterlyData: any[]): string[] {
      if (!quarterlyData || quarterlyData.length === 0) {
         return [];
      }

      // Collect all unique quarters from all data items
      const allQuarters = new Set<string>();
      quarterlyData.forEach(item => {
         if (item.data) {
            Object.keys(item.data).forEach(quarter => {
               allQuarters.add(quarter);
            });
         }
      });

      // Convert to array and sort quarters chronologically
      const sortedQuarters = Array.from(allQuarters).sort((a, b) => {
         // Parse quarter format like "Q1'25" to sort chronologically
         const parseQuarter = (q: string) => {
            const [quarter, year] = q.split("'");
            const quarterNum = parseInt(quarter.substring(1));
            const fullYear = parseInt(year) + 2000; // Convert '25 to 2025
            return fullYear * 4 + quarterNum; // Create sortable number
         };

         return parseQuarter(a) - parseQuarter(b); // Sort ascending (oldest first)
      });

      // Return the latest 4 quarters (last 4 from the sorted array)
      return sortedQuarters.slice(-4);
   }

   toggleAccordion(row: string): void {
      if (this.openRows.has(row)) {
         this.openRows.delete(row); // Close the accordion
      } else {
         this.openRows.add(row); // Open the accordion
      }
   }

   isAccordionOpen(row: string): boolean {
      return this.openRows.has(row); // Check if the accordion is open
   }
   hasData(data: any): boolean {
      return this.quarters.some(
         (q) => data[q] !== undefined && data[q] !== null
      );
   }

   hasAnnualData(
      data: { label: string; data: Record<string, number> }[]
   ): boolean {
      return data.some((item) =>
         Object.keys(item.data).some((year) =>
            this.years.includes(Number(year))
         )
      );
   }

   hasQuarterlyData(
      quarterlyData: { label: string; data: Record<string, number> }[]
   ): boolean {
      return quarterlyData.some((item) =>
         Object.keys(item.data).some((quarter) =>
            this.quarters.includes(quarter)
         )
      );
   }
   formatValue = (value: number | string | undefined): string => {
      if (value === undefined || value === null || value === '') {
         return '-';
      }

      // Convert string values to numbers
      let numericValue = typeof value === 'string' ? parseFloat(value) : value;

      // Handle NaN cases (e.g., invalid strings)
      if (isNaN(numericValue)) {
         return '-';
      }

      // Multiply by 10,00,000
      numericValue *= 1_000_000;

      const absValue = Math.abs(numericValue);
      const formattedValue = (val: number) =>
         val.toFixed(2).replace(/\.?0+$/, '');

      let result: string;

      // Handle billions
      if (absValue >= 1_000_000_000) {
         result = formattedValue(absValue / 1_000_000_000) + ' B';
      }
      // Handle millions
      else if (absValue >= 1_000_000) {
         result = formattedValue(absValue / 1_000_000) + ' M';
      }
      // Handle decimal numbers
      else {
         result = formattedValue(absValue);
      }

      // Show negative values in parentheses without a negative sign
      return numericValue < 0 ? `(${result})` : result;
   };


   // toggleChildren(item: any) {
   //    item.expanded = !item.expanded;
   //    console.log("Toggled item:", item); // Debugging statement
   //  }

   hasData2(data: any): boolean {
      return data && Object.keys(data).length > 0;
   }

   trackByFn(index: number, item: any): any {
      return item.id; // Use a unique identifier for each item
   }
   toggleItem(label: string): void {
      if (this.expandedItems.has(label)) {
         this.expandedItems.delete(label);
      } else {
         this.expandedItems.add(label);
      }
   }

   isItemExpanded(label: string): boolean {
      return this.expandedItems.has(label);
   }
   expanded: boolean = false;
   toggleExpand() {
      this.expanded = !this.expanded;
   }
}
